// Trim, delete and levelling of own recordings on this device (2026-10-06).
//
// - Saving a trim: the edit (JSON) goes through the outbox like every other change
//   (PUT /api/recordings/:id/edit), and every cached list shows it at once. The edit is rendered
//   here (engine audio/trim.ts renderEdit, levelled to the common speech level), encoded the way
//   the app records (showaudio/encode.ts: AAC/MP4 or Opus/WebM, WAV as the fallback) and kept in
//   the "recording-renders" cache until it is uploaded (PUT /rendered?rev=). The upload waits until
//   the edit itself has left the outbox, and retries when the connection returns. Until it
//   arrives, everyone hears the previous audio (Recording.editPending says so to the owner).
//   Encoding is faster than real time where the browser has WebCodecs (showaudio/fast-encode.ts).
// - A fresh take (not uploaded yet) is trimmed on the device: renderTake gives the trimmed file,
//   which is then uploaded the usual way (RecordCompare).
// - Restoring the original: PUT edit null (outbox); the rendered copy is dropped everywhere.
// - Delete: soft on the server (routes/recordings.ts): hidden at once in every list here, "Kumoa"
//   for UNDO_MS (a restore queued after the delete), then the cached audio goes.
// - Levelling: the first time one of our recordings plays on this device without a measured gain,
//   its audio is measured (measureLoudness) and the gain stored locally and on the server.
import type { Recording, RecordingEdit } from '@valyrio/engine/api';
import { measureLoudness, renderEdit, type EditSpans } from '@valyrio/engine/audio/trim';
import type { Pcm } from '@valyrio/engine/audio/types';
import { api, ApiError } from '../lib/api';
import { getRemote, mutateRemote, registerPendingOverlayPrefix, remoteKeys } from '../lib/remote';
import { forgetRecordingAudio, pendingWrites, queueWrite } from '../state/actions';
import { store } from '../state/store';
import { dropStaleCopies, noteRecording, recordingIdOfUrl, recordingRev, recordingGain, setRecordingGain } from './audio';
import { reapplyVolume } from './volume';

export const RENDERS_CACHE = 'recording-renders';
/** "Äänite poistettu · Kumoa" stays this long. */
export const UNDO_MS = 10_000;

const editPath = (id: string) => `/api/recordings/${encodeURIComponent(id)}/edit`;
const recPath = (id: string) => `/api/recordings/${encodeURIComponent(id)}`;

/** A show clip (Sarjan äänet) is the show's voice, not a take of ours: never trimmed. */
export const editableTarget = (target: string): boolean => !target.startsWith('show:');

// ---------- every cached list that shows a recording ----------

const LIST_PREFIX = 'recordings:';

/** Apply `fn` to the recording `id` in every recording list in memory (null removes it). */
export function patchRecording(id: string, fn: (r: Recording) => Recording | null): void {
  for (const key of remoteKeys(LIST_PREFIX)) {
    const data = getRemote<Recording[]>(key).data;
    if (!Array.isArray(data) || !data.some((r) => r.id === id)) continue;
    mutateRemote<Recording[]>(key, (d) =>
      (d ?? []).flatMap((r) => {
        if (r.id !== id) return [r];
        const next = fn(r);
        return next ? [next] : [];
      }),
    );
  }
}

/** Put a recording back into the lists it was in (after "Kumoa"). */
function reinsert(rec: Recording, keys: readonly string[]): void {
  for (const key of keys) {
    mutateRemote<Recording[]>(key, (d) => [rec, ...(d ?? []).filter((r) => r.id !== rec.id)].sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
  }
}

function withEdit(r: Recording, edit: RecordingEdit | null): Recording {
  const { edit: _e, editPending: _p, ...rest } = r;
  return edit ? { ...rest, edit, editPending: true } : rest;
}

// Queued writes not on the server yet, laid over a fresh list (lib/remote.ts): a delete hides the
// row, an edit shows on it. In queue order; the last write per recording wins.
registerPendingOverlayPrefix<Recording[]>(LIST_PREFIX, async (list) => {
  const writes = [...(await pendingWrites('recording')), ...(await pendingWrites('recording-edit'))].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  if (!writes.length) return list;
  const deleted = new Set<string>();
  const edits = new Map<string, RecordingEdit | null>();
  for (const w of writes) {
    const m = /^\/api\/recordings\/([^/]+)(\/edit|\/restore)?$/.exec(w.path);
    if (!m) continue;
    const id = decodeURIComponent(m[1]!);
    if (w.method === 'DELETE' && !m[2]) deleted.add(id);
    else if (m[2] === '/restore') deleted.delete(id);
    else if (m[2] === '/edit') edits.set(id, (w.body as { edit?: RecordingEdit | null } | undefined)?.edit ?? null);
  }
  return list.filter((r) => !deleted.has(r.id)).map((r) => (edits.has(r.id) ? withEdit(r, edits.get(r.id)!) : r));
});

// ---------- trim ----------

/** A new edit revision id (the API accepts [A-Za-z0-9_-]{4,40}). */
export function newRev(): string {
  const a = new Uint8Array(6);
  try {
    crypto.getRandomValues(a);
  } catch {
    for (let i = 0; i < a.length; i++) a[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(a, (b) => b.toString(36).padStart(2, '0'))
    .join('')
    .slice(0, 10);
}

export interface SaveDeps {
  encode: (samples: Float32Array, rate: number) => Promise<Blob>;
}

const defaultEncode = async (samples: Float32Array, rate: number): Promise<Blob> => (await import('../showaudio/encode')).encodeClip(samples, rate);

/**
 * The edited audio, levelled: the kept segments joined, then the gain that brings the speech to
 * the common level (never past the peak ceiling). Returns the PCM and the gain left to apply at
 * playback (about 1).
 */
export function renderLevelled(pcm: Pcm, spans: EditSpans): { pcm: Pcm; gain: number } {
  const plain = renderEdit(pcm, spans, 1);
  const level = measureLoudness(plain);
  const out = level.gain === 1 ? plain : renderEdit(pcm, spans, level.gain);
  return { pcm: out, gain: Math.min(1, Math.max(0.1, measureLoudness(out).gain)) };
}

/** Save a trim: queue the edit, show it everywhere, render and queue the rendered audio. */
export async function saveTrim(rec: Recording, spans: EditSpans, original: Pcm, deps: SaveDeps = { encode: defaultEncode }): Promise<RecordingEdit> {
  const dur = Math.round((original.samples.length / original.sampleRate) * 1000) / 1000;
  const edit: RecordingEdit = { v: 1, start: spans.start, end: spans.end, cuts: spans.cuts.map(([a, b]) => [a, b]), dur, rev: newRev() };
  patchRecording(rec.id, (r) => withEdit(r, edit));
  await queueWrite('PUT', editPath(rec.id), { edit }, 'recording-edit');
  const { pcm, gain } = renderLevelled(original, spans);
  const blob = await deps.encode(pcm.samples, pcm.sampleRate);
  await dropRenders(rec.id);
  await keepRender(rec.id, edit.rev, gain, blob);
  void flushRenders();
  return edit;
}

/**
 * A fresh take trimmed on this device before it is sent (RecordCompare): rendered and encoded the
 * same way as a saved trim. The caller keeps the take as recorded, so the trim can still be
 * changed or undone until the take is uploaded; what is uploaded is this file.
 */
export async function renderTake(original: Pcm, spans: EditSpans, deps: SaveDeps = { encode: defaultEncode }): Promise<Blob> {
  const { pcm } = renderLevelled(original, spans);
  return deps.encode(pcm.samples, pcm.sampleRate);
}

/** "Palauta alkuperäinen": the original plays again everywhere. */
export async function restoreOriginal(rec: Recording): Promise<void> {
  patchRecording(rec.id, (r) => withEdit(r, null));
  await dropRenders(rec.id);
  await queueWrite('PUT', editPath(rec.id), { edit: null }, 'recording-edit');
  await forgetRecordingAudio(rec.id);
}

// ---------- the rendered audio, kept until it is uploaded ----------

const renderUrl = (id: string, rev: string) => `/__recording-render/${encodeURIComponent(id)}?rev=${encodeURIComponent(rev)}`;

async function keepRender(id: string, rev: string, gain: number, blob: Blob): Promise<void> {
  if (typeof caches === 'undefined') {
    memory.set(`${id} ${rev}`, { id, rev, gain, blob });
    return;
  }
  const c = await caches.open(RENDERS_CACHE);
  await c.put(renderUrl(id, rev), new Response(blob, { headers: { 'Content-Type': blob.type || 'audio/wav', 'X-Gain': String(gain) } }));
}

/** Without Cache Storage (tests, odd browsers): kept for this page only. */
const memory = new Map<string, { id: string; rev: string; gain: number; blob: Blob }>();

async function dropRenders(id: string): Promise<void> {
  for (const k of memory.keys()) if (k.startsWith(`${id} `)) memory.delete(k); // deleting while iterating a Map is safe
  if (typeof caches === 'undefined') return;
  try {
    const c = await caches.open(RENDERS_CACHE);
    for (const req of await c.keys()) {
      if (decodeURIComponent(new URL(req.url).pathname.split('/').pop() ?? '') === id) await c.delete(req);
    }
  } catch {
    /* nothing kept */
  }
}

interface Pending {
  id: string;
  rev: string;
  gain: number;
  blob: () => Promise<Blob>;
  done: () => Promise<void>;
}

async function pendingRenders(): Promise<Pending[]> {
  const out: Pending[] = [...memory.entries()].map(([k, v]) => ({
    id: v.id,
    rev: v.rev,
    gain: v.gain,
    blob: async () => v.blob,
    done: async () => {
      memory.delete(k);
    },
  }));
  if (typeof caches === 'undefined') return out;
  try {
    const c = await caches.open(RENDERS_CACHE);
    for (const req of await c.keys()) {
      const u = new URL(req.url);
      const id = decodeURIComponent(u.pathname.split('/').pop() ?? '');
      const rev = u.searchParams.get('rev') ?? '';
      const res = await c.match(req);
      if (!res || !id || !rev) continue;
      const gain = Number(res.headers.get('X-Gain')) || 1;
      out.push({
        id,
        rev,
        gain,
        blob: () => res.clone().blob(),
        done: async () => {
          await c.delete(req);
        },
      });
    }
  } catch {
    /* no cache */
  }
  return out;
}

const RETRY_MS = [5_000, 15_000, 60_000, 300_000];
let retryAt = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let flushing: Promise<void> | null = null;

/** Upload the rendered trims waiting on this device (once their edit has left the outbox). */
export function flushRenders(): Promise<void> {
  flushing ??= (async () => {
    try {
      const list = await pendingRenders();
      if (!list.length) {
        retryAt = 0;
        return;
      }
      if (store.get().identity === 'demo') return;
      const queued = new Set((await pendingWrites('recording-edit')).map((w) => w.path));
      let again = false;
      for (const p of list) {
        if (queued.has(editPath(p.id))) {
          again = true; // the edit itself is still on its way
          continue;
        }
        try {
          const saved = await api.uploadRendered(p.id, p.rev, p.gain, await p.blob());
          await p.done();
          noteRecording(saved);
          patchRecording(p.id, (r) => ({ ...saved, ...(r.marks ? { marks: r.marks } : {}) }));
        } catch (e) {
          const err = e instanceof ApiError ? e : null;
          // a newer edit, a restored original, a deleted recording: this render is not needed
          if (err && (err.status === 409 || err.status === 404 || err.status === 403)) await p.done();
          else again = true;
        }
      }
      if (again) schedule();
      else retryAt = 0;
    } finally {
      flushing = null;
    }
  })();
  return flushing;
}

function schedule(): void {
  if (retryTimer) return;
  const wait = RETRY_MS[Math.min(retryAt, RETRY_MS.length - 1)]!;
  retryAt++;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void flushRenders();
  }, wait);
}

let started = false;
/** Upload waiting renders now and whenever the connection returns (from main.tsx). */
export function startRenderQueue(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('online', () => {
    retryAt = 0;
    void flushRenders();
  });
  // after each sync: a queued edit may just have reached the server
  let lastSync = store.get().sync.lastSync;
  store.subscribe(() => {
    const s = store.get().sync;
    if (s.lastSync === lastSync) return;
    lastSync = s.lastSync;
    if (s.state === 'idle') void flushRenders();
  });
  setTimeout(() => void flushRenders(), 3_000);
}

// ---------- delete with undo ----------

export interface Deleted {
  rec: Recording;
  /** The lists it was removed from (put back by Kumoa). */
  keys: string[];
  until: number;
  /** Its Kumoa is shown beside the take that was deleted (Voice Dock), not in the shell's toast. */
  inline?: boolean;
}

const deletedListeners = new Set<() => void>();
let lastDeleted: Deleted | null = null;
let undoTimer: ReturnType<typeof setTimeout> | null = null;

export function lastDeletedRecording(): Deleted | null {
  return lastDeleted;
}
export function subscribeDeleted(l: () => void): () => void {
  deletedListeners.add(l);
  return () => deletedListeners.delete(l);
}
function setDeleted(d: Deleted | null): void {
  lastDeleted = d;
  for (const l of deletedListeners) l();
}

/** "Poista äänite": hidden at once everywhere, the delete queued; Kumoa for UNDO_MS. */
export async function deleteRecording(rec: Recording, undoMs = UNDO_MS, opts: { inline?: boolean } = {}): Promise<void> {
  const keys = remoteKeys(LIST_PREFIX).filter((k) => getRemote<Recording[]>(k).data?.some((r) => r.id === rec.id));
  patchRecording(rec.id, () => null);
  // the partner's word marks on it go with it (they are hidden by the server too)
  if (getRemote('recording-marks').data !== undefined)
    mutateRemote<{ recordingId: string }[]>('recording-marks', (d) => (d ?? []).filter((m) => m.recordingId !== rec.id));
  if (lastDeleted) await finishDelete(lastDeleted);
  setDeleted({ rec, keys, until: Date.now() + undoMs, ...(opts.inline ? { inline: true } : {}) });
  if (undoTimer) clearTimeout(undoTimer);
  undoTimer = setTimeout(() => {
    if (lastDeleted?.rec.id === rec.id) void finishDelete(lastDeleted);
  }, undoMs);
  await queueWrite('DELETE', recPath(rec.id), undefined, 'recording');
}

/** The inline Kumoa left the screen (its sheet closed): the shell's toast takes over for the time left. */
export function releaseInlineUndo(id: string): void {
  if (lastDeleted?.rec.id === id && lastDeleted.inline) setDeleted({ ...lastDeleted, inline: false });
}

/** Kumoa: the restore is queued after the delete (the server keeps it for its undo window). */
export async function undoDelete(): Promise<void> {
  const d = lastDeleted;
  if (!d) return;
  if (undoTimer) clearTimeout(undoTimer);
  undoTimer = null;
  setDeleted(null);
  reinsert(d.rec, d.keys);
  await queueWrite('POST', `${recPath(d.rec.id)}/restore`, undefined, 'recording');
}

async function finishDelete(d: Deleted): Promise<void> {
  if (lastDeleted === d) setDeleted(null);
  await dropRenders(d.rec.id);
  await forgetRecordingAudio(d.rec.id);
}

// ---------- levelling: measure a recording's gain the first time it plays ----------

const measuring = new Set<string>();

/** Called for every element that starts playing (recordings/volume.ts). */
export function measureOnPlay(el: HTMLMediaElement): void {
  const src = el.currentSrc || el.src || '';
  const id = recordingIdOfUrl(src);
  if (!id || recordingGain(id) !== undefined || measuring.has(id)) return;
  if (store.get().identity === 'demo') return;
  measuring.add(id);
  void (async () => {
    try {
      const rev = recordingRev(id);
      const res = await fetch(src, { credentials: 'same-origin' });
      if (!res.ok || !(res.headers.get('content-type') ?? '').startsWith('audio/')) return;
      const { decodeToPcm } = await import('../pronunciation/analysis');
      const gain = measureLoudness(await decodeToPcm(await res.blob())).gain;
      setRecordingGain(id, rev, gain);
      reapplyVolume();
      await queueWrite('PUT', `/api/recordings/${encodeURIComponent(id)}/gain`, { gain, ...(rev ? { audioRev: rev } : {}) }, 'recording-gain');
    } catch {
      /* not measured: it plays at the device volume */
    }
  })();
}

/** A deleted recording's player: was it deleted (404), or is it just not reachable? */
export async function audioGone(src: string): Promise<boolean> {
  try {
    const res = await fetch(src, { method: 'HEAD', credentials: 'same-origin', cache: 'no-store' });
    return res.status === 404;
  } catch {
    return false;
  }
}

export { dropStaleCopies };
